import { useAuth } from 'hooks/useAuth'
import { useMutation } from 'hooks/useMutation'
import { useQuery } from 'hooks/useQuery'
import { useQueryClient } from 'hooks/useQueryClient'

import { defaultNotificationSettings } from 'utils/defaults/notificationSettings'
import { formatError } from 'utils/formatError'
import { Log } from 'utils/logger'
import { LogRocket } from 'utils/logrocket'
import { fetchUser, postUser } from 'utils/queries'
import { Toast } from 'utils/toast'
import { hasCompletedOnboarding } from 'utils/user'

import { Media } from 'types/media'
import { UserType } from 'types/user'

export const useCurrentUser = () => {
  const queryClient = useQueryClient()
  const { data: authData, ready: authReady } = useAuth()
  const currentAuthUserId = authData?.uid

  const userQuery = useQuery<UserType>(
    ['user', currentAuthUserId],
    async () => {
      const user = await fetchUser({ userId: currentAuthUserId })
      if (!user) {
        throw new Error('No user found')
      }

      if (user.type === 'trainer') {
        user.isTrainer = true
      }

      if (!user.notificationSettings) {
        user.notificationSettings = defaultNotificationSettings
      } else if (
        !user.notificationSettings.find((setting) => setting.identifier === 'follow-posts')
      ) {
        // Add the default setting for posts by users that this user follows if it doesn't exist.
        // This is needed because this setting was not part of the original batch and might not exist.
        user.notificationSettings.push(
          defaultNotificationSettings.find((setting) => setting.identifier === 'follow-posts'),
        )
      } else if (
        !user.notificationSettings.find((setting) => setting.identifier === 'follow-activities')
      ) {
        user.notificationSettings.push(
          defaultNotificationSettings.find((setting) => setting.identifier === 'follow-activities'),
        )
      } else if (
        !user.notificationSettings.find((setting) => setting.identifier === 'upspace-activities')
      ) {
        user.notificationSettings.push(
          defaultNotificationSettings.find(
            (setting) => setting.identifier === 'upspace-activities',
          ),
        )
      }

      return user
    },
    {
      // The query will not execute until the currentAuthUserId exists
      enabled: !!authReady && !!currentAuthUserId,
      onSuccess: (user) => {
        LogRocket.identify(user.id, {
          name: `${user.firstName} ${user.lastName}`,
          username: user.username,
          type: user.type,
        })
      },
      retry: true,
    },
  )

  const updateUser = useMutation(
    (variables: { data: UserType & { avatar?: Media } }) =>
      postUser({
        userId: currentAuthUserId,
        updatedOrCreatedData: variables?.data,
      }),
    {
      onSuccess: (newUserData) => {
        queryClient.setQueryData(['user', currentAuthUserId], () => {
          const currentUserData = userQuery.data
          if (newUserData) {
            return {
              ...currentUserData,
              ...newUserData,
            }
          }
          return currentUserData
        })
      },
      onError: (err) => {
        const formattedError = formatError(err)
        Toast.error('We had trouble updating the user', formattedError)
        Log.error('useMutation onError', err)
      },
    },
  )

  return {
    ...userQuery,
    meta: {
      onboardingComplete: hasCompletedOnboarding(userQuery.data),
      loggedIn: authReady && !!userQuery.data,
    },

    // Mutation Objects
    update: updateUser,
  }
}
